/**
 * photo-upload.js — Museum Shadow Box Photo Upload
 * ES6+ class-based, zero dependencies.
 * Supports ImgBB and Cloudinary unsigned upload.
 * Injects Shopify line item properties into the product form.
 * Handles mobile grid fallback alongside the absolute-positioned shadow box.
 */

(function () {
  'use strict';

  /* ─────────────────────────────────────────────────────────────
     MuseumPhotoUpload — main class
  ───────────────────────────────────────────────────────────── */
  class MuseumPhotoUpload {
    /**
     * @param {HTMLElement} rootEl  - .museum-section element
     */
    constructor(rootEl) {
      this.root         = rootEl;
      this.config       = this._readConfig();
      this.slots        = [];     // array of SlotState objects
      this.productForm  = null;   // the Shopify <form action="/cart/add">
      this.hiddenInputs = {};     // map: slotIndex -> hidden <input>

      this._init();
    }

    /* ── Config ─────────────────────────────────────────────── */
    _readConfig() {
      const d = this.root.dataset;
      return {
        uploadService:       d.uploadService                || 'imgbb',
        imgbbApiKey:         d.imgbbApiKey                  || '',
        cloudinaryCloudName: d.cloudinaryCloudName          || '',
        cloudinaryPreset:    d.cloudinaryPreset             || '',
        maxSizeMb:           parseFloat(d.maxSizeMb)        || 10,
        requiredCount:       parseInt(d.requiredCount, 10)  || 0,
      };
    }

    /* ── Initialise ─────────────────────────────────────────── */
    _init() {
      this._buildMobileGrid();
      this._buildSlots();
      this._bindFormIntegration();
      this._buildReview();
      this._renderSummary();
    }

    /* ── Mobile grid fallback ───────────────────────────────── */
    /**
     * Clone all .museum-frame elements into a flat grid container
     * that is shown only on screens <=480px via CSS.
     * We share the same slot state objects, just with two DOM nodes each.
     */
    _buildMobileGrid() {
      // Mobile grid disabled — same shadow box on all devices
    }

    /* ── Build slot state & events ──────────────────────────── */
    _buildSlots() {
      const primaryFrames = Array.from(
        this.root.querySelectorAll('.museum-stage .museum-frame')
      );

      primaryFrames.forEach((el) => {
        const idx = parseInt(el.dataset.slotIndex, 10);

        const state = {
          index:    idx,
          el,
          mobileEl: null,
          file:     null,
          url:      null,
          status:   'idle',
          label:    el.dataset.slotLabel || `תמונה ${idx + 1}`,
          required: el.dataset.required === 'true',
        };

        this.slots.push(state);
        this._bindFrameEvents(state, el, false);
      });
    }

    /* ── Bind events for one frame DOM node ─────────────────── */
    _bindFrameEvents(state, frameEl, isMobile) {
      const input     = frameEl.querySelector('.museum-frame__input');
      const removeBtn = frameEl.querySelector('.museum-frame__remove');

      // Click → open file picker (only when empty)
      frameEl.addEventListener('click', (e) => {
        if (removeBtn && (e.target === removeBtn || removeBtn.contains(e.target))) return;
        if (state.status === 'uploading') return;
        if (!state.url) input?.click();
      });

      // Keyboard accessibility
      frameEl.addEventListener('keydown', (e) => {
        if ((e.key === 'Enter' || e.key === ' ') && !state.url) {
          e.preventDefault();
          input?.click();
        }
      });

      // File input change
      input?.addEventListener('change', (e) => {
        const file = e.target.files?.[0];
        if (file) this._handleFile(state, file);
        e.target.value = ''; // reset so same file can be reselected
      });

      // Remove button
      removeBtn?.addEventListener('click', (e) => {
        e.stopPropagation();
        this._clearSlot(state);
      });

      // Drag & drop
      frameEl.addEventListener('dragover', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (state.status !== 'uploading') {
          frameEl.classList.add('is-dragover');
        }
      });

      frameEl.addEventListener('dragleave', (e) => {
        e.stopPropagation();
        frameEl.classList.remove('is-dragover');
      });

      frameEl.addEventListener('drop', (e) => {
        e.preventDefault();
        e.stopPropagation();
        frameEl.classList.remove('is-dragover');
        if (state.status === 'uploading') return;
        const file = e.dataTransfer?.files?.[0];
        if (file) this._handleFile(state, file);
      });
    }

    /* ── Handle selected / dropped file ─────────────────────── */
    async _handleFile(state, file) {
      const allowedTypes = [
        'image/jpeg', 'image/png', 'image/webp',
        'image/heic', 'image/gif',
      ];

      if (!allowedTypes.includes(file.type)) {
        this._setSlotError(state, 'סוג קובץ לא נתמך. יש להעלות תמונה (JPG, PNG, WEBP, GIF).');
        return;
      }

      const maxBytes = this.config.maxSizeMb * 1024 * 1024;
      if (file.size > maxBytes) {
        this._setSlotError(
          state,
          `הקובץ גדול מדי (${this._formatSize(file.size)}). הגודל המרבי הוא ${this.config.maxSizeMb}MB.`
        );
        return;
      }

      state.file = file;
      this._clearSlotError(state);

      // Show local thumbnail immediately for fast feedback
      const localUrl = URL.createObjectURL(file);
      this._setThumb(state, localUrl);
      this._setUploading(state, true);

      try {
        const cdnUrl = await this._uploadFile(file);
        state.url = cdnUrl;
        // Swap local blob URL for the CDN URL
        this._setThumb(state, cdnUrl);
        this._setUploading(state, false);
        this._injectHiddenInput(state);
        this._clearGlobalError();
        this._renderSummary();
      } catch (err) {
        console.error('[MuseumPhotoUpload] Upload error:', err);
        // The local preview is already shown; only the hosted SAVE failed.
        this._setUploading(state, false);
        const m = (err && err.message) || '';
        const isConfig = /not configured|not set|api key|preset/i.test(m);
        if (isConfig) {
          console.warn('[MuseumPhotoUpload] Image hosting is NOT configured. The merchant must set an ImgBB API Key (or a Cloudinary cloud name + unsigned preset) in this section’s settings, otherwise uploaded photos cannot be saved to the order.');
          this._setSlotError(state, 'התמונה נבחרה ✓ — אך שמירת התמונות עדיין לא הוגדרה בחנות.');
        } else {
          this._setSlotError(state, 'שמירת התמונה נכשלה — נסו שוב בעוד רגע.');
        }
        state.url = null;
        this._removeHiddenInput(state);
        this._renderSummary();
      }
    }

    /* ── Upload dispatcher ──────────────────────────────────── */
    _uploadFile(file) {
      return this.config.uploadService === 'cloudinary'
        ? this._uploadToCloudinary(file)
        : this._uploadToImgBB(file);
    }

    /* ── ImgBB Upload ───────────────────────────────────────── */
    async _uploadToImgBB(file) {
      if (!this.config.imgbbApiKey) {
        throw new Error('ImgBB API key not configured');
      }

      // Resize large images before upload for speed
      const resizedFile = await this._resizeIfNeeded(file, 2000);

      const formData = new FormData();
      formData.append('key',   this.config.imgbbApiKey);
      formData.append('image', resizedFile);

      const resp = await fetch('https://api.imgbb.com/1/upload', {
        method: 'POST',
        body:   formData,
      });

      if (!resp.ok) {
        throw new Error(`ImgBB error ${resp.status}: ${await resp.text()}`);
      }

      const json = await resp.json();
      if (!json.success) {
        throw new Error('ImgBB upload failed: ' + JSON.stringify(json));
      }

      return json.data.url;
    }

    /* ── Resize image if too large (for faster uploads) ───── */
    _resizeIfNeeded(file, maxDim) {
      return new Promise((resolve) => {
        if (file.size < 500 * 1024) { resolve(file); return; }

        const img = new Image();
        const url = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(url);
          const w = img.naturalWidth, h = img.naturalHeight;
          if (w <= maxDim && h <= maxDim) { resolve(file); return; }

          const scale = Math.min(maxDim / w, maxDim / h);
          const nw = Math.round(w * scale), nh = Math.round(h * scale);

          const canvas = document.createElement('canvas');
          canvas.width = nw; canvas.height = nh;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, nw, nh);

          canvas.toBlob((blob) => {
            resolve(blob || file);
          }, 'image/jpeg', 0.85);
        };
        img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
        img.src = url;
      });
    }

    /* ── Cloudinary Unsigned Upload ─────────────────────────── */
    async _uploadToCloudinary(file) {
      const { cloudinaryCloudName: cloud, cloudinaryPreset: preset } = this.config;
      if (!cloud || !preset) {
        throw new Error('Cloudinary cloud name or upload preset not configured');
      }

      const resizedFile = await this._resizeIfNeeded(file, 2000);

      const formData = new FormData();
      formData.append('file',          resizedFile);
      formData.append('upload_preset', preset);
      formData.append('folder',        'museum-uploads');

      const resp = await fetch(
        `https://api.cloudinary.com/v1_1/${cloud}/image/upload`,
        { method: 'POST', body: formData }
      );

      if (!resp.ok) {
        throw new Error(`Cloudinary error ${resp.status}: ${await resp.text()}`);
      }

      const json = await resp.json();
      if (json.error) throw new Error('Cloudinary error: ' + json.error.message);

      return json.secure_url;
    }

    /* ── Utility: file → base64 data URL ────────────────────── */
    _fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('FileReader error'));
        reader.readAsDataURL(file);
      });
    }

    /* ── Set thumbnail on both DOM nodes ────────────────────── */
    _setThumb(state, url) {
      state.displayUrl = url;
      [state.el, state.mobileEl].forEach((frameEl) => {
        if (!frameEl) return;
        const thumb = frameEl.querySelector('.museum-frame__thumb');
        if (thumb) {
          if (thumb.tagName === 'IMG') {
            // Force a distortion-proof "cover" fit with !important, re-applied on
            // load, so the store theme's own img rules/scripts can't stretch it.
            const fit = () => {
              thumb.style.setProperty('width', '100%', 'important');
              thumb.style.setProperty('height', '100%', 'important');
              thumb.style.setProperty('max-width', 'none', 'important');
              thumb.style.setProperty('object-fit', 'cover', 'important');
              thumb.style.setProperty('object-position', 'center', 'important');
            };
            thumb.onload = fit;
            thumb.src = url;
            fit();
          } else {
            thumb.style.setProperty('background-image', `url("${url}")`, 'important');
          }
        }
        frameEl.classList.add('has-image');
        frameEl.setAttribute('aria-pressed', 'true');
      });
      this._renderReview();
    }

    /* ── Toggle uploading state ─────────────────────────────── */
    _setUploading(state, uploading) {
      state.status = uploading ? 'uploading' : (state.url ? 'done' : 'idle');
      [state.el, state.mobileEl].forEach((frameEl) => {
        if (!frameEl) return;
        frameEl.classList.toggle('is-uploading', uploading);
      });
    }

    /* ── Clear a slot (remove photo) ────────────────────────── */
    _clearSlot(state) {
      state.file       = null;
      state.url        = null;
      state.displayUrl = null;
      state.status     = 'idle';

      [state.el, state.mobileEl].forEach((frameEl) => {
        if (!frameEl) return;
        frameEl.classList.remove('has-image', 'is-uploading', 'has-error', 'is-dragover');
        frameEl.setAttribute('aria-pressed', 'false');
        const thumb = frameEl.querySelector('.museum-frame__thumb');
        if (thumb) {
          if (thumb.tagName === 'IMG') { thumb.src = ''; }
          else { thumb.style.backgroundImage = ''; }
        }
      });

      this._clearSlotError(state);
      this._removeHiddenInput(state);
      this._clearGlobalError();
      this._renderSummary();
      this._renderReview();
    }

    /* ── Slot-level error ───────────────────────────────────── */
    _setSlotError(state, msg) {
      [state.el, state.mobileEl].forEach((frameEl) => {
        if (!frameEl) return;
        frameEl.classList.add('has-error');
        // Force reflow for re-triggering animation
        void frameEl.offsetWidth;
      });

      this._showGlobalError(msg);

      // Auto-clear after 4 s
      setTimeout(() => {
        [state.el, state.mobileEl].forEach(el => el?.classList.remove('has-error'));
        if (state.status === 'idle' || state.status === 'error') {
          state.status = 'idle';
          this._clearGlobalError();
        }
      }, 4000);
    }

    _clearSlotError(state) {
      [state.el, state.mobileEl].forEach(el => el?.classList.remove('has-error'));
    }

    /* ── Global error banner ────────────────────────────────── */
    _showGlobalError(msg) {
      const banner = this.root.querySelector('.museum-error-banner');
      const text   = this.root.querySelector('.museum-error-banner__text');
      if (!banner) return;
      if (text) text.textContent = msg;
      banner.classList.add('is-visible');
    }

    _clearGlobalError() {
      this.root.querySelector('.museum-error-banner')?.classList.remove('is-visible');
    }

    /* ── Progress summary ───────────────────────────────────── */
    _renderSummary() {
      const countEl = this.root.querySelector('.museum-section__summary-count');
      const uploaded = this.slots.filter(s => s.url).length;
      if (countEl) countEl.textContent = `${uploaded} / ${this.slots.length}`;

      // "Selected photos" button — count what the customer has picked (shown).
      const picked = this.slots.filter(s => s.displayUrl).length;
      if (this.reviewBtn) {
        this.reviewBtn.hidden = picked === 0;
        const c = this.reviewBtn.querySelector('.museum-review-btn__count');
        if (c) c.textContent = String(picked);
      }
    }

    /* ── "Selected photos" review/edit gallery ──────────────── */
    _buildReview() {
      this.reviewBtn = this.root.querySelector('.museum-review-btn');
      this.modal     = this.root.querySelector('.museum-modal');
      if (!this.reviewBtn || !this.modal) return;

      this.reviewBtn.addEventListener('click', () => this._openReview());
      this.modal.querySelectorAll('[data-close]').forEach((el) =>
        el.addEventListener('click', () => this._closeReview()));
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.modal && !this.modal.hidden) this._closeReview();
      });
    }

    _openReview() {
      if (!this.modal) return;
      this._renderReview();
      this.modal.hidden = false;
      document.body.style.overflow = 'hidden';
    }

    _closeReview() {
      if (!this.modal) return;
      this.modal.hidden = true;
      document.body.style.overflow = '';
    }

    _renderReview() {
      if (!this.modal) return;
      const container = this.modal.querySelector('.museum-modal__grid, .museum-modal__carousel');
      if (!container) return;
      container.innerHTML = '';
      container.className = 'museum-modal__carousel';

      const prevBtn = document.createElement('button');
      prevBtn.type = 'button';
      prevBtn.className = 'museum-modal__arrow museum-modal__arrow--prev';
      prevBtn.innerHTML = '&#8250;';
      prevBtn.setAttribute('aria-label', 'הקודם');

      const nextBtn = document.createElement('button');
      nextBtn.type = 'button';
      nextBtn.className = 'museum-modal__arrow museum-modal__arrow--next';
      nextBtn.innerHTML = '&#8249;';
      nextBtn.setAttribute('aria-label', 'הבא');

      const slidesWrap = document.createElement('div');
      slidesWrap.className = 'museum-modal__slides';

      const counter = document.createElement('div');
      counter.className = 'museum-modal__counter';

      this.slots.forEach((state) => {
        const slide = document.createElement('div');
        slide.className = 'museum-modal__slide';

        const media = document.createElement('div');
        media.className = 'museum-modal__slide-media';
        if (state.displayUrl) {
          const img = document.createElement('img');
          img.alt = state.label;
          img.src = state.displayUrl;
          media.appendChild(img);
        } else {
          const empty = document.createElement('span');
          empty.className = 'museum-modal__slide-empty';
          empty.textContent = 'טרם נבחרה תמונה';
          media.appendChild(empty);
        }

        const label = document.createElement('div');
        label.className = 'museum-modal__slide-label';
        label.textContent = state.label;

        const actions = document.createElement('div');
        actions.className = 'museum-modal__slide-actions';

        const changeBtn = document.createElement('button');
        changeBtn.type = 'button';
        changeBtn.className = 'museum-modal__slide-btn';
        changeBtn.textContent = state.displayUrl ? 'שנה' : 'הוסף תמונה';
        changeBtn.addEventListener('click', () => {
          const input = state.el && state.el.querySelector('.museum-frame__input');
          if (input) input.click();
        });
        actions.appendChild(changeBtn);

        if (state.displayUrl) {
          const removeBtn = document.createElement('button');
          removeBtn.type = 'button';
          removeBtn.className = 'museum-modal__slide-btn museum-modal__slide-btn--remove';
          removeBtn.textContent = 'הסר';
          removeBtn.addEventListener('click', () => {
            this._clearSlot(state);
            this._renderReview();
          });
          actions.appendChild(removeBtn);
        }

        slide.appendChild(media);
        slide.appendChild(label);
        slide.appendChild(actions);
        slidesWrap.appendChild(slide);
      });

      container.appendChild(prevBtn);
      container.appendChild(slidesWrap);
      container.appendChild(nextBtn);
      container.appendChild(counter);

      let current = this._reviewIndex || 0;
      if (current >= this.slots.length) current = 0;

      const update = () => {
        slidesWrap.style.transform = `translateX(${current * 100}%)`;
        counter.textContent = `${current + 1} / ${this.slots.length}`;
        prevBtn.disabled = current >= this.slots.length - 1;
        nextBtn.disabled = current <= 0;
        this._reviewIndex = current;
      };

      prevBtn.addEventListener('click', () => { if (current < this.slots.length - 1) { current++; update(); } });
      nextBtn.addEventListener('click', () => { if (current > 0) { current--; update(); } });

      this._reviewKeyHandler && document.removeEventListener('keydown', this._reviewKeyHandler);
      this._reviewKeyHandler = (e) => {
        if (this.modal.hidden) return;
        if (e.key === 'ArrowRight' && current < this.slots.length - 1) { current++; update(); }
        if (e.key === 'ArrowLeft' && current > 0) { current--; update(); }
      };
      document.addEventListener('keydown', this._reviewKeyHandler);

      let touchStartX = 0;
      slidesWrap.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
      slidesWrap.addEventListener('touchend', (e) => {
        const diff = touchStartX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          if (diff < 0 && current < this.slots.length - 1) { current++; update(); }
          else if (diff > 0 && current > 0) { current--; update(); }
        }
      }, { passive: true });

      update();
    }

    /* ── Shopify form integration ───────────────────────────── */
    _bindFormIntegration() {
      this.productForm =
        this.root.closest('form[action*="/cart/add"]') ||
        document.querySelector('form[action="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]');

      if (!this.productForm) {
        console.warn('[MuseumPhotoUpload] No product form found on page.');
      } else {
        this.productForm.addEventListener('submit', this._onFormSubmit.bind(this));
      }

      this._interceptCartAjax();
    }

    /* ── Get photo properties object ───────────────────────── */
    _getPhotoProperties() {
      const props = {};
      this.slots.forEach(s => {
        if (s.url) props[`_${s.label}`] = s.url;
      });
      return props;
    }

    /* ── Intercept fetch & XHR to /cart/add ─────────────────── */
    _interceptCartAjax() {
      const self = this;

      const origFetch = window.fetch;
      window.fetch = function(url, opts) {
        const urlStr = (typeof url === 'string') ? url : (url && url.url) || '';
        if (urlStr.includes('/cart/add') && opts && opts.body) {
          try {
            const props = self._getPhotoProperties();
            if (Object.keys(props).length > 0) {
              if (typeof opts.body === 'string') {
                const data = JSON.parse(opts.body);
                if (data.items) {
                  data.items.forEach(item => {
                    item.properties = Object.assign({}, item.properties || {}, props);
                  });
                } else if (data.id) {
                  data.properties = Object.assign({}, data.properties || {}, props);
                }
                opts = Object.assign({}, opts, { body: JSON.stringify(data) });
              } else if (opts.body instanceof FormData) {
                Object.entries(props).forEach(([k, v]) => {
                  opts.body.set('properties[' + k + ']', v);
                });
              }
            }
          } catch (e) { /* skip injection on parse error */ }
        }
        return origFetch.apply(this, arguments);
      };

      const origOpen = XMLHttpRequest.prototype.open;
      const origSend = XMLHttpRequest.prototype.send;
      XMLHttpRequest.prototype.open = function(method, url) {
        this._museumUrl = url;
        return origOpen.apply(this, arguments);
      };
      XMLHttpRequest.prototype.send = function(body) {
        if (this._museumUrl && this._museumUrl.includes('/cart/add') && typeof body === 'string') {
          try {
            const props = self._getPhotoProperties();
            if (Object.keys(props).length > 0) {
              const data = JSON.parse(body);
              if (data.items) {
                data.items.forEach(item => {
                  item.properties = Object.assign({}, item.properties || {}, props);
                });
              } else if (data.id) {
                data.properties = Object.assign({}, data.properties || {}, props);
              }
              body = JSON.stringify(data);
            }
          } catch (e) { /* skip */ }
        }
        return origSend.call(this, body);
      };
    }

    /* ── Intercept form submit ──────────────────────────────── */
    _onFormSubmit(e) {
      this.slots.forEach(s => {
        if (s.url) this._injectHiddenInput(s);
      });

      const missing = this.slots.slice(0, this.config.requiredCount).filter(s => !s.url);

      if (missing.length > 0) {
        e.preventDefault();
        e.stopImmediatePropagation();

        const names = missing.map(s => s.label).join(', ');
        this._showGlobalError(
          `יש להעלות את התמונות הנדרשות לפני ההוספה לסל: ${names}`
        );

        this.root.scrollIntoView({ behavior: 'smooth', block: 'start' });

        missing.forEach(s => {
          [s.el, s.mobileEl].forEach((frameEl) => {
            if (!frameEl) return;
            frameEl.classList.remove('has-error');
            void frameEl.offsetWidth;
            frameEl.classList.add('has-error');
            setTimeout(() => frameEl.classList.remove('has-error'), 600);
          });
        });

        return false;
      }
    }

    /* ── Inject / update hidden input in product form ─────── */
    _injectHiddenInput(state) {
      if (!this.productForm) return;

      const propName = `properties[_${state.label}]`;
      let input = this.hiddenInputs[state.index];

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = propName;
        this.productForm.appendChild(input);
        this.hiddenInputs[state.index] = input;
      }

      input.value = state.url;
    }

    /* ── Remove hidden input ────────────────────────────────── */
    _removeHiddenInput(state) {
      const input = this.hiddenInputs[state.index];
      if (input) {
        input.remove();
        delete this.hiddenInputs[state.index];
      }
    }

    /* ── Utility: format file size ──────────────────────────── */
    _formatSize(bytes) {
      if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
      return `${(bytes / 1024).toFixed(0)}KB`;
    }
  }

  /* ─────────────────────────────────────────────────────────────
     Auto-initialise
  ───────────────────────────────────────────────────────────── */
  function initAll() {
    document.querySelectorAll('.museum-section').forEach((el) => {
      if (!el.dataset.puInit) {
        el.dataset.puInit = '1';
        new MuseumPhotoUpload(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Shopify theme editor re-renders sections via XHR
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target?.querySelector('.museum-section');
    if (!section) return;
    delete section.dataset.puInit;
    new MuseumPhotoUpload(section);
  });

  // Expose for manual init / testing
  window.MuseumPhotoUpload = MuseumPhotoUpload;
})();

